import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '@env/environment';
import { firstValueFrom } from 'rxjs';
import { AuthService } from '@core/auth/services/auth.service';
import { AppModeService } from '@core/services/app-mode.service';
import { DiccionarioService } from '@core/services/diccionario.service';
import { IndiceService } from '@features/admin/indice/services/indice.service';

/**
 * Prefijos de ruta que, en el HOST DE UN CONGRESO, activan el modo admin "de congreso"
 * (Secretaría / administración del congreso) en lugar de la web pública.
 *
 * - `/admin`: URL de entrada que se le da a Secretaría (`<host-congreso>/admin`); redirige a
 *   `/auth/login` (ver app.routes.ts).
 * - `/auth` y `/app`: necesarios para que un F5 dentro de la administración o el logout
 *   (que lleva a `/auth/login`) sigan resolviendo modo admin y no caigan al 404 de `/web`.
 *
 * El congreso se resuelve igual que en modo público (Indice/urlcongreso), y es el que el
 * login usa para buscar a Secretaría en la BD de su congreso (antes: sgiindice fijo).
 * Acordado en la reunión interna del 02/09/2026.
 */
const PREFIJOS_ADMIN_CONGRESO = ['/admin', '/auth', '/app'];

/** Devuelve true si el pathname empieza por alguno de los prefijos (`/admin`, `/admin/...`). */
export function esRutaAdminCongreso(pathname: string): boolean {
   const ruta = String(pathname || '').toLowerCase();
   return PREFIJOS_ADMIN_CONGRESO.some(p => ruta === p || ruta.startsWith(p + '/'));
}

export function appModeInitializer(
   appModeService: AppModeService,
   congresoServiceIndice: IndiceService,
   authService: AuthService,
   diccionarioService: DiccionarioService
): () => Promise<void> {
   return async () => {
      const currentHost = window.location.hostname.toLowerCase();
      const adminHost = String(environment.DOMINIO_ADMIN || '').toLowerCase();

      if (currentHost === adminHost) {
         appModeService.setAdmin();
      } else {
         // Desarrollo local: localhost, 127.0.0.1 y *.localhost (p. ej. uncongreso.localhost)
         // no existen en sgi_indice, así que se resuelve contra el host de pruebas del
         // environment (URL_CONGRESO_HOST). En prod/demo ese valor va vacío y se usa
         // siempre el dominio real del navegador.
         const esLocalhost =
            currentHost === 'localhost' ||
            currentHost === '127.0.0.1' ||
            currentHost.endsWith('.localhost');
         const hostCongreso = esLocalhost && environment.URL_CONGRESO_HOST
            ? String(environment.URL_CONGRESO_HOST).toLowerCase()
            : currentHost;

         try {
            const congreso = await firstValueFrom(congresoServiceIndice.resolveCongresoByUrl(hostCongreso, true));

            if (congreso?.id) {
               authService.setSgiindice(Number(congreso.id));
               // Mismo host de congreso, dos zonas: `/web` (público) y `/admin|/auth|/app`
               // (administración del congreso: Secretaría). El congreso resuelto es el mismo.
               if (esRutaAdminCongreso(window.location.pathname)) {
                  appModeService.setAdminCongreso(congreso);
               } else {
                  appModeService.setCongreso(congreso);
                  // Textos fijos traducidos ANTES de pintar la primera pantalla (reunión 14/10/2026).
                  // Solo llama al back si el congreso es multiidioma y el idioma no es el origen;
                  // si falla, la web sale en el idioma origen.
                  try {
                     await firstValueFrom(diccionarioService.cargar());
                  } catch {
                     // Sin diccionario: textos por defecto del código.
                  }
               }
            } else {
               appModeService.setUnknown('sin-congreso');
            }
         } catch (error) {
            // 404 de Indice/urlcongreso = host no dado de alta en el índice; cualquier otra cosa
            // (red, 500...) = no se pudo hablar con la API. La página 404 lo muestra como referencia.
            const sinCongreso = error instanceof HttpErrorResponse && error.status === 404;
            appModeService.setUnknown(sinCongreso ? 'sin-congreso' : 'sin-api');
         }
      }
   };
}
